import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { WalletService } from '../../../core/services/wallet.service';
import { MerchantContextService } from '../../../core/services/merchant-context.service';

interface Tab {
  label: string;
  icon: string;
  route: string;
  /** Top-level URL segment that keeps this tab lit, so detail pages don't unhighlight it. */
  section: string;
}

/**
 * Mobile tab bar.
 *
 * A drawer alone makes the app feel like a website on a phone: every move costs a tap to open
 * the menu first. A persistent tab bar is what puts the top-level sections one thumb away.
 * Hidden above the mobile breakpoint, where the sidebar already does this job.
 */
@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, RouterLink, MatIconModule],
  templateUrl: './bottom-nav.component.html',
  styleUrls: ['./bottom-nav.component.scss']
})
export class BottomNavComponent {
  private readonly router = inject(Router);
  private readonly wallet = inject(WalletService);
  private readonly context = inject(MerchantContextService);

  /**
   * Four of the six sections, plus More. Compliance and Settings live behind More: six targets
   * across a narrow phone leaves each one too small to hit reliably.
   */
  protected readonly tabs: Tab[] = [
    { label: 'Home',     icon: 'dashboard',              route: '/dashboard',            section: 'dashboard' },
    { label: 'Treasury', icon: 'account_balance_wallet', route: '/treasury/holdings',    section: 'treasury' },
    { label: 'Business', icon: 'corporate_fare',         route: '/business/overview',    section: 'business' },
    { label: 'Reports',  icon: 'assessment',             route: '/reports/intelligence', section: 'reports' },
  ];

  /** Individual has no Business tab, matching the sidebar. */
  protected readonly visibleTabs = computed(() => this.tabs.filter(tab => this.context.showsSection(tab.section)));

  protected readonly activeSection = signal(sectionOf(this.router.url));

  /** True when the current page is behind More, so that tab lights up instead. */
  protected readonly inMoreSection = signal(isMoreSection(sectionOf(this.router.url)));

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const section = sectionOf(this.router.url);
        this.activeSection.set(section);
        this.inMoreSection.set(isMoreSection(section));
      });
  }

  protected openMenu(): void {
    this.wallet.toggleMobileSidebar();
  }
}

function sectionOf(url: string): string {
  return url.split('?')[0].split('/')[1] ?? '';
}

function isMoreSection(section: string): boolean {
  return section === 'compliance' || section === 'settings' || section === 'help-support';
}
